<template>
  <div
    v-if="isGroup"
    data-slot="condition-snapshot-group"
    :class="[ 'flex flex-col gap-2', level > 1 && 'rounded-xl border border-border bg-background/40 p-3' ]"
  >
    <!-- eslint-disable-next-line vue/no-v-for-template-key -->
    <template v-for="(item, index) in node.items" :key="'node-' + index">
      <condition-snapshot
        :node="item"
        :fields="fields"
        :comparisons="comparisons"
        :ad-account="adAccount"
        :level="level + 1"
      />

      <div v-if="index < node.items.length - 1" :key="'connector-' + index" class="flex items-center gap-2.5">
        <span class="h-px flex-1 bg-border" />
        <span :class="[ 'rounded-full px-3 py-0.5 text-xs font-semibold tracking-wide text-white', logicClass === 'and' ? 'bg-primary' : 'bg-primary/45' ]">
          {{ tr(node.logic) }}
        </span>
        <span class="h-px flex-1 bg-border" />
      </div>
    </template>
  </div>

  <div
    v-else
    data-slot="condition-snapshot-row"
    :class="[ 'glass-tile flex flex-col gap-2 rounded-lg border px-3.5 py-2.5', passed ? 'border-success/60' : 'border-destructive/40 opacity-60' ]"
  >
    <div class="flex flex-wrap items-center gap-1.5 text-sm">
      <circle-check-icon v-if="passed" class="size-3.5 shrink-0 text-success" />
      <circle-x-icon v-else class="size-3.5 shrink-0 text-destructive" />
      <span class="font-semibold text-foreground">{{ fieldLabel }}</span>
      <span v-if="datePeriodLabel" class="text-xs text-muted-foreground">{{ datePeriodLabel }}</span>
      <span v-if="node.subfield" class="text-xs text-muted-foreground">· {{ subfieldLabel }}</span>
      <template v-if="isTextField">
        <span class="text-muted-foreground">{{ comparisonLabel }}</span>
        <span class="font-semibold [overflow-wrap:anywhere] text-foreground">{{ searchTerm }}</span>
      </template>
    </div>

    <template v-if="isTextField">
      <div v-if="textExcerpt" class="text-xs leading-snug text-muted-foreground">
        {{ textExcerpt.prefix }}
        {{ textExcerpt.before }}
        <mark class="bg-transparent font-semibold text-success">{{ textExcerpt.matched }}</mark>
        {{ textExcerpt.after }}
        {{ textExcerpt.suffix }}
      </div>
    </template>

    <template v-else-if="isDynamic">
      <div class="flex flex-col gap-0.5">
        <span class="text-xs text-muted-foreground">{{ tr('Requirement') }}</span>
        <span class="text-sm text-foreground">{{ requirementText }}</span>
      </div>
      <div class="flex flex-col gap-0.5">
        <span class="text-xs text-muted-foreground">{{ tr('Measured value') }}</span>
        <span :class="[ 'text-lg font-semibold', passed ? 'text-success' : 'text-destructive' ]">{{ measuredText }}</span>
      </div>
    </template>

    <div v-else class="flex gap-5">
      <div class="flex flex-1 flex-col gap-0.5">
        <span class="text-xs text-muted-foreground">{{ tr('Measured value') }}</span>
        <span :class="[ 'text-lg font-semibold', passed ? 'text-success' : 'text-destructive' ]">{{ measuredText }}</span>
      </div>
      <div class="flex flex-1 flex-col gap-0.5">
        <span class="text-xs text-muted-foreground">{{ tr('Requirement') }}</span>
        <span class="text-lg font-semibold text-foreground">{{ requirementText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { CircleCheck, CircleX } from '@lucide/vue';

import AdAccount from '@/entities/AdAccount';
import ConditionLeafMixin from '@/mixins/ConditionLeaf.js';

export default {
  name: 'ConditionSnapshot',
  components: {
    CircleCheckIcon: CircleCheck,
    CircleXIcon: CircleX,
  },
  mixins: [ ConditionLeafMixin ],
  props: {
    node: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    comparisons: {
      type: Array,
      default: () => [],
    },
    adAccount: {
      type: [ AdAccount, null ],
      default: null,
    },
    level: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    passed() {
      return this.node.result === true;
    },
    measuredText() {
      return this.formatConditionValue(this.node.current_value, this.field, this.comparison, this.formatContext);
    },
    textExcerpt() {
      if (!this.isTextField) {
        return null;
      }

      return buildConditionTextExcerpt(this.node.current_value, this.node.value, this.node.comparison);
    },
  },
};

const EXCERPT_CONTEXT_CHARS = 35;

/**
 * For a string/text condition, build a short, word-bounded excerpt of the
 * measured post text around the first occurrence of the search term.
 *
 * Returns a structured part object { prefix, before, matched, after, suffix }
 * (rendered as plain text + a highlighted <mark> in the template, so no v-html),
 * or null when there is nothing useful to show:
 *  - the goal is "does not contain" (we only care that it's absent),
 *  - the term is not found (no match to highlight),
 *  - missing text/term.
 */
function buildConditionTextExcerpt(text, term, comparison) {
  if (comparison === 'not.contains') {
    return null;
  }

  const haystackText = text != null ? String(text) : '';
  const searchTerm = term != null ? String(term) : '';
  if (!haystackText || !searchTerm) {
    return null;
  }

  const matchIndex = findConditionTextMatchIndex(haystackText, searchTerm, comparison);
  if (matchIndex === -1) {
    return null;
  }

  const matchEnd = matchIndex + searchTerm.length;
  let start = Math.max(0, matchIndex - EXCERPT_CONTEXT_CHARS);
  let end = Math.min(haystackText.length, matchEnd + EXCERPT_CONTEXT_CHARS);

  // Snap the context edges to spaces so we don't show half words around the match.
  if (start > 0) {
    const space = haystackText.indexOf(' ', start);
    if (space !== -1 && space < matchIndex) {
      start = space + 1;
    }
  }
  if (end < haystackText.length) {
    const space = haystackText.lastIndexOf(' ', end);
    if (space !== -1 && space > matchEnd) {
      end = space;
    }
  }

  return {
    prefix: start > 0 ? '… ' : '',
    before: haystackText.slice(start, matchIndex),
    matched: haystackText.slice(matchIndex, matchEnd),
    after: haystackText.slice(matchEnd, end),
    suffix: end < haystackText.length ? ' …' : '',
  };
}

// Locate the search term inside the measured text the same way the backend
// comparison does, returning the char index of the match (or -1):
//  - contains_exact: the term must equal a whole, whitespace-delimited word (case-sensitive).
//  - contains (and any other): case-insensitive substring.
function findConditionTextMatchIndex(text, term, comparison) {
  if (comparison === 'contains_exact') {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = new RegExp('(?:^|\\s)(' + escaped + ')(?=\\s|$)').exec(text);
    if (!match) {
      return -1;
    }
    return match.index + match[0].length - term.length;
  }

  return text.toLowerCase().indexOf(term.toLowerCase());
}
</script>
